import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { JawaMark } from "@/components/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState("verifying");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    const timer = setInterval(async () => {
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          clearInterval(timer);
        } else if (attempts.current > 20) {
          setState("failed");
          clearInterval(timer);
        }
      } catch {
        if (attempts.current > 20) {
          setState("failed");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  const download = async () => {
    try {
      const res = await api.get(`/orders/${sessionId}/download`, { responseType: "blob" });
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "jawa-guide.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your guide is downloading.");
    } catch {
      toast.error("Your guide is being prepared — our concierge will email it to you shortly.");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-jawa-ivory px-6" data-testid="payment-success-page">
      <div className="w-full max-w-md border border-jawa-stone bg-white p-10 text-center shadow-sm">
        <JawaMark className="mx-auto h-12 w-12 text-jawa-espresso" />
        {state === "verifying" && (
          <>
            <Loader2 className="mx-auto mt-8 h-8 w-8 animate-spin text-jawa-terracotta" />
            <h1 className="mt-6 font-serif text-3xl font-medium text-jawa-espresso">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-jawa-espresso/60">This takes just a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mt-8 h-10 w-10 text-jawa-terracotta" />
            <h1 className="mt-6 font-serif text-3xl font-medium text-jawa-espresso">Thank you.</h1>
            <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/60">
              Your payment is confirmed. Your JÀWÀ A–Z Guide is ready to download — we've also
              emailed you a receipt with your download link, so you can retrieve it any time.
            </p>
            <button
              onClick={download}
              data-testid="download-guide-button"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-jawa-espresso px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-terracotta"
            >
              <Download className="h-4 w-4" /> Download Your Guide
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <h1 className="mt-6 font-serif text-3xl font-medium text-jawa-espresso">Still confirming…</h1>
            <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/60">
              We could not confirm your payment yet. If you completed checkout, your guide
              will be confirmed shortly — otherwise please try again.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="mt-8 block text-xs uppercase tracking-[0.25em] text-jawa-espresso/50 transition-colors hover:text-jawa-terracotta"
        >
          Back to JÀWÀ
        </Link>
      </div>
    </div>
  );
}
